/**
 * Shared placement solver for toolbox drag-and-drop.
 *
 * Both the live drag preview and the actual drop call this so the ghost the
 * operator sees while dragging is exactly the element that gets committed —
 * same derived dimensions, same endpoint snap, same connector fit, same
 * non-overlap separation.
 *
 * Naming and id allocation stay with the caller: those reserve values as a side
 * effect and must not run for a preview.
 */
import {
  ElementType,
  GridSettings,
  LayoutElement,
  ToolboxItem,
} from '../types';
import { ELEMENT_COLORS } from '../constants';
import { DEFAULT_LAYER_FOR_TYPE } from '../features/layers/layersSlice';
import { getTrackStrokeWidth } from './canvasElementRendering';
import { CONNECTOR_COLOR, fitConnectorToParallelRails } from './connectorRail';
import { snapPositionToGrid } from './geometry';
import { metersToEffectiveWorldUnits } from './measurement';
import {
  ALL_RAIL_TYPES,
  ENDPOINT_TOLERANCE,
  SNAP_DRAG_TOLERANCE,
  findOHTLaneSnapPosition,
  findRailSnapOffset,
  findDockSnapPosition,
  shouldAutoDockSnap,
  shouldSkipEquipmentEditorSnap,
  getCircleJunctionConnectedPortIndices,
  getCircleJunctionMaxPorts,
  getCircleJunctionSpokesLocal,
  getEquipmentSpacingPosition,
  getObjectEdgeSnapPosition,
  getRailEndpoints,
  getRailNonOverlappingDropPosition,
  resolveRailType,
} from './topology';

/** Placeholder id used while an element is only a preview. */
export const DROP_PREVIEW_ID = '__drop_preview__';

export interface ToolboxDropContext {
  /** Every element already on the canvas. */
  elements: LayoutElement[];
  gridSettings: GridSettings;
  /** Floor-plan calibration, when a calibrated DXF is loaded. */
  scaleFactor?: number;
  dxfUnitsPerMeter?: number;
  /** Manually placed vehicle stops, used by stocker snapping. */
  stops?: unknown[];
  /** Display name for the element. Previews can pass a placeholder. */
  name: string;
}

/**
 * A circle-junction spoke stub that is currently hidden but would be drawn once
 * the dragged element lands, because its port becomes connected. This is the
 * diagonal leg that appears between the hub and a 45° port.
 */
export interface JunctionStubPreview {
  /** World-space polyline `[tipX, tipY, ringX, ringY]`. */
  points: number[];
  color: string;
  /** Stroke width in world units, matching the junction's own rendering. */
  strokeWidth: number;
}

export interface ToolboxDropPlacement {
  /** Fully positioned element, still carrying {@link DROP_PREVIEW_ID}. */
  element: LayoutElement;
  isRail: boolean;
  /** Resolved `railType` custom property, or '' for non-rails. */
  railType: string;
  layerId: string;
  /**
   * Endpoint the placement snapped onto (a junction port or a free rail end),
   * or null when the element landed unattached. Drives the snap highlight.
   */
  snapPoint: { x: number; y: number } | null;
  /** Junction legs that the drop would reveal. Empty when nothing connects. */
  junctionStubs: JunctionStubPreview[];
}

/**
 * Endpoint shared between the placed element and an existing rail. Recomputed
 * from the final geometry rather than returned by the snap helpers, so it also
 * catches placements that were already touching before snapping.
 */
function findSharedEndpoint(
  placed: LayoutElement,
  others: LayoutElement[],
  tolerance: number
): { x: number; y: number } | null {
  const myEnds = getRailEndpoints(placed);
  let best: { x: number; y: number; dist: number } | null = null;

  for (const other of others) {
    if (
      other.id === placed.id ||
      !ALL_RAIL_TYPES.has(other.type) ||
      !other.metadata.visible
    ) {
      continue;
    }
    for (const me of myEnds) {
      for (const oe of getRailEndpoints(other)) {
        const dist = Math.hypot(oe.x - me.x, oe.y - me.y);
        if (dist <= tolerance && (!best || dist < best.dist)) {
          best = { x: oe.x, y: oe.y, dist };
        }
      }
    }
  }

  return best ? { x: best.x, y: best.y } : null;
}

/**
 * Junction legs that are hidden now but would render once `placed` exists.
 *
 * Circle junctions only draw a spoke when a rail tip abuts that port, so a
 * dropped track lights up the leg between the hub and the port it lands on.
 * Reusing {@link getCircleJunctionConnectedPortIndices} keeps the preview in
 * step with the junction renderer's own visibility rule.
 */
function findRevealedJunctionStubs(
  placed: LayoutElement,
  elements: LayoutElement[],
  scaleFactor?: number,
  dxfUnitsPerMeter?: number
): JunctionStubPreview[] {
  const stubs: JunctionStubPreview[] = [];

  for (const junction of elements) {
    if (
      !junction.metadata.visible ||
      !ALL_RAIL_TYPES.has(junction.type) ||
      resolveRailType(junction) !== 'junction-circle'
    ) {
      continue;
    }

    const fromGhost = getCircleJunctionConnectedPortIndices(junction, [placed]);
    if (fromGhost.size === 0) continue;
    const alreadyDrawn = getCircleJunctionConnectedPortIndices(junction, elements);

    const { width, height } = junction.dimensions;
    const spokes = getCircleJunctionSpokesLocal(
      width,
      height,
      getCircleJunctionMaxPorts(junction)
    );
    const rot = ((junction.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const toWorld = (lx: number, ly: number) => ({
      x: junction.position.x + lx * cos - ly * sin,
      y: junction.position.y + lx * sin + ly * cos,
    });

    for (const index of fromGhost) {
      if (alreadyDrawn.has(index)) continue;
      const spoke = spokes[index];
      if (!spoke) continue;
      const tip = toWorld(spoke.tipX, spoke.tipY);
      const ring = toWorld(spoke.ringX, spoke.ringY);
      stubs.push({
        points: [tip.x, tip.y, ring.x, ring.y],
        color: junction.metadata.color || '#cccccc',
        strokeWidth: getTrackStrokeWidth(junction, scaleFactor, dxfUnitsPerMeter),
      });
    }
  }

  return stubs;
}

/**
 * Resolve where a dragged toolbox item would land, including every snap the
 * commit path applies.
 */
export function computeToolboxDropPlacement(
  item: ToolboxItem,
  worldPt: { x: number; y: number },
  context: ToolboxDropContext,
  propertyOverrides?: Record<string, unknown>
): ToolboxDropPlacement {
  const { elements, gridSettings, scaleFactor, dxfUnitsPerMeter, stops } = context;
  const toWorldUnits = (meters: number) =>
    metersToEffectiveWorldUnits(meters, scaleFactor, dxfUnitsPerMeter);

  const isRail = ALL_RAIL_TYPES.has(item.type);
  const dropDefaultProperties = {
    ...(item.defaultProperties || {}),
    ...(propertyOverrides || {}),
  } as Record<string, unknown>;

  const skipEquipmentGrid =
    item.type === 'rectangle' ||
    item.type === 'stb' ||
    item.type === 'ohb' ||
    item.type === 'lifter';
  const position =
    gridSettings.snapToGrid && item.type !== 'oht-vehicle' && !isRail && !skipEquipmentGrid
      ? snapPositionToGrid({ x: worldPt.x, y: worldPt.y }, gridSettings.snapGridSize)
      : { x: worldPt.x, y: worldPt.y };

  const dims = { ...item.defaultDimensions };
  const dropRailType = String(dropDefaultProperties.railType || '');
  const isStraightDrop = item.type === 'overhead-rail' && dropRailType === 'straight';
  const isConnectorDrop = item.type === 'overhead-rail' && dropRailType === 'connector';
  const isStraightLikeDrop = isStraightDrop || isConnectorDrop;

  if (isStraightLikeDrop) {
    const configuredMeters = Number(dropDefaultProperties.lengthMeters);
    const lengthMeters =
      Number.isFinite(configuredMeters) && configuredMeters > 0
        ? configuredMeters
        : isConnectorDrop
          ? 1
          : 3;
    dims.width = Math.max(1, toWorldUnits(lengthMeters));
  }

  if (isStraightDrop) {
    const thicknessM = Number(dropDefaultProperties.thicknessMeters);
    if (Number.isFinite(thicknessM) && thicknessM > 0) {
      dims.height = Math.max(0.001, toWorldUnits(thicknessM));
    }
  }

  // H-connector lateral span comes from shiftOffsetMeters (distance between rails).
  if (isConnectorDrop) {
    const spanM = Number(dropDefaultProperties.shiftOffsetMeters);
    const spanMeters = Number.isFinite(spanM) && spanM > 0 ? spanM : 2;
    dims.height = Math.max(1, toWorldUnits(spanMeters));
  }

  if (
    (item.type === 'overhead-rail' || item.type === 'overhead-curve') &&
    (dropRailType === 'curve90' ||
      dropRailType === 'curve45' ||
      dropRailType === 'curve180')
  ) {
    const radiusM = Number(dropDefaultProperties.radiusMeters);
    if (Number.isFinite(radiusM) && radiusM > 0) {
      const radiusUnits = Math.max(0.001, toWorldUnits(radiusM));
      dims.width = radiusUnits;
      dims.height = radiusUnits;
    }
  }

  if (item.type === 'stocker' || dropDefaultProperties.objectType === 'stocker') {
    const count = String(dropDefaultProperties.bayCount || '4');
    let widthM = 2.645;
    if (count === '6') widthM = 3.36;
    else if (count === '8') widthM = 4.075;
    dims.width = Math.max(1, toWorldUnits(widthM));
  }

  const equipmentModel = String(dropDefaultProperties.equipmentModel ?? '').toUpperCase();
  if (equipmentModel.includes('AWP-300') || equipmentModel.includes('AWP300')) {
    dims.width = Math.max(1, toWorldUnits(1.8));
    dims.height = Math.max(1, toWorldUnits(1.75));
  }

  const layerId = DEFAULT_LAYER_FOR_TYPE[item.type as string] ?? 'layer-1';
  const defaultRotation =
    typeof item.defaultRotation === 'number' ? item.defaultRotation : 0;

  const tempElement: LayoutElement = {
    id: DROP_PREVIEW_ID,
    type: item.type as ElementType,
    layerId,
    position,
    rotation: defaultRotation,
    dimensions: dims,
    metadata: {
      name: context.name,
      locked: false,
      visible: true,
      color:
        dropRailType === 'connector'
          ? CONNECTOR_COLOR
          : ELEMENT_COLORS[item.type as ElementType],
    },
    customProperties: {
      sku: item.sku,
      ...dropDefaultProperties,
    },
  };

  let finalPosition = position;
  let finalRotation = defaultRotation;
  let finalDims = { ...dims };
  let finalProps = { ...dropDefaultProperties };

  if (isRail) {
    const offset = findRailSnapOffset(tempElement, elements);
    if (offset) {
      finalPosition = { x: position.x + offset.dx, y: position.y + offset.dy };
    }
    // H-connector: auto-span between the nearest pair of parallel rails.
    // Skip AABB separation — the H must overlap both host rails.
    if (isConnectorDrop) {
      const fitted = fitConnectorToParallelRails(
        { ...tempElement, position: finalPosition },
        elements,
        toWorldUnits
      );
      finalPosition = fitted.position;
      finalRotation = fitted.rotation;
      finalDims = { ...finalDims, ...fitted.dimensions };
      finalProps = { ...finalProps, shiftOffsetMeters: fitted.shiftOffsetMeters };
    } else {
      finalPosition = getRailNonOverlappingDropPosition(
        { ...tempElement, position: finalPosition },
        elements
      );
    }
  } else if (item.type === 'oht-vehicle') {
    const snapPos = findOHTLaneSnapPosition(tempElement, elements);
    if (snapPos) {
      finalPosition = { x: snapPos.x, y: snapPos.y };
      finalRotation = snapPos.rotation;
    }
  } else if (shouldAutoDockSnap(tempElement)) {
    const snapPos = findDockSnapPosition(
      tempElement,
      elements,
      SNAP_DRAG_TOLERANCE,
      stops
    );
    if (snapPos) {
      finalPosition = { x: snapPos.x, y: snapPos.y };
      finalRotation = snapPos.rotation;
    }
  } else if (
    gridSettings.snapToObjects &&
    !shouldSkipEquipmentEditorSnap(tempElement)
  ) {
    finalPosition = getObjectEdgeSnapPosition(
      { ...tempElement, position: finalPosition },
      elements
    );
  }

  if (!isRail && !shouldSkipEquipmentEditorSnap(tempElement)) {
    // Runs after every snap so a dock or lane snap can't park the new tool on
    // top of one already standing there.
    finalPosition = getEquipmentSpacingPosition(
      { ...tempElement, position: finalPosition, rotation: finalRotation },
      elements,
      { alongDockLine: shouldAutoDockSnap(tempElement) }
    );
  }

  const element: LayoutElement = {
    ...tempElement,
    position: finalPosition,
    rotation: finalRotation,
    dimensions: finalDims,
    metadata: { ...tempElement.metadata, layerId },
    customProperties: { sku: item.sku, ...finalProps },
  };

  return {
    element,
    isRail,
    railType: dropRailType,
    layerId,
    snapPoint: isRail ? findSharedEndpoint(element, elements, ENDPOINT_TOLERANCE) : null,
    junctionStubs: isRail
      ? findRevealedJunctionStubs(element, elements, scaleFactor, dxfUnitsPerMeter)
      : [],
  };
}
